import React, { createContext, useContext, useMemo, useReducer } from 'react';

interface FilterTag {
  alias: string;
  legId: number;
  type?: string;
  undo?: any | any[];
}

const OrderFilterTagsContext = createContext<FilterTag[]>([]);
const ActionsOrderContext = createContext({
  add: Function.prototype,
  remove: Function.prototype,
  removeLast: Function.prototype,
  clear: Function.prototype,
  clearAllStartsWith: Function.prototype,
  dispatch: Function.prototype,
});

const someLegIdIsUndefined = (a, b) => [a.legId, b.legId].includes(undefined);
const isEqual = (a, b) =>
  a.alias === b.alias && (someLegIdIsUndefined(a, b) || a.legId === b.legId);

export const useOrderFilterTags = () => {
  return useContext(OrderFilterTagsContext);
};

export const useOrderFilterTagsActions = () => {
  return useContext(ActionsOrderContext);
};

const findTagIndex = (tags, tag) =>
  tags.findIndex((currentTag) => isEqual(currentTag, tag));

const update = (updatableIndex, fn, tags) =>
  tags.map((tag, index) => (index === updatableIndex ? fn(tag) : tag));

const addReducer = (orderedTags, { tag }) => {
  const index = findTagIndex(orderedTags, tag);
  if (index < 0) {
    return orderedTags.concat([tag]);
  } else if (tag.type !== orderedTags[index]) {
    return update(
      index,
      (currentTag) => ({
        ...currentTag,
        undo: Array.isArray(currentTag.undo)
          ? currentTag.undo
          : [currentTag.undo, tag.undo],
      }),
      orderedTags
    );
  }
  return orderedTags;
};

const removeReducer = (orderedTags, { tag }) => {
  const index = findTagIndex(orderedTags, tag);
  return index >= 0
    ? orderedTags.slice(0, index).concat(orderedTags.slice(index + 1))
    : orderedTags;
};

const removeLastReducer = (orderedTags) => orderedTags.slice(0, -1);

const clearGroupReducer = (orderedTags, { tag }) =>
  orderedTags.filter((item) => !item.alias.startsWith(tag.alias));

const reducer = (orderedTags, action) => {
  switch (action.type) {
    case 'ADD':
      return addReducer(orderedTags, action);
    case 'REMOVE':
      return removeReducer(orderedTags, action);
    case 'REMOVE_LAST':
      return removeLastReducer(orderedTags);
    case 'CLEAR_GROUP':
      return clearGroupReducer(orderedTags, action);
    default:
      return orderedTags;
  }
};

export default function OrderedFilterTagsProvider({ children }) {
  const [orderedTags, dispatch] = useReducer(reducer, []);

  const actions = useMemo(() => {
    const createAction = (type) => (tag) =>
      dispatch({
        tag,
        type,
      });
    const clearGroup = createAction('CLEAR_GROUP');
    return {
      add: createAction('ADD'),
      remove: createAction('REMOVE'),
      removeLast: createAction('REMOVE_LAST'),
      clear: createAction('CLEAR'),
      clearAllStartsWith: (alias) => clearGroup({ alias }),
      dispatch,
    };
  }, [dispatch]);
  return (
    <ActionsOrderContext.Provider value={actions}>
      <OrderFilterTagsContext.Provider value={orderedTags}>
        {children}
      </OrderFilterTagsContext.Provider>
    </ActionsOrderContext.Provider>
  );
}
